import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  IconButton,
  makeStyles,
  Paper,
  TextField,
  Typography
} from "@material-ui/core";
import { Add, CheckCircleOutline, DeleteOutline } from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import BlockDocument from "../../components/BlockDocument";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";

const emptyContent = { type: "blocknote", blocks: [] };

const createEmptyNote = () => ({
  id: null,
  title: "",
  content: emptyContent
});

const getBlocksText = blocks =>
  (blocks || [])
    .flatMap(block => block.content || [])
    .map(item => item.text || "")
    .filter(Boolean)
    .join(" ");

const hasDocumentContent = content => {
  const blocks = Array.isArray(content?.blocks) ? content.blocks : [];
  return blocks.some(block => {
    if (!block) return false;
    if (block.type === "table") return true;
    if (block.type !== "paragraph") return true;
    return (block.content || []).some(item => String(item?.text || "").trim());
  });
};

const getDocumentSignature = content => {
  try {
    return JSON.stringify(content || emptyContent);
  } catch {
    return "";
  }
};

const sortNotes = notes =>
  [...notes].sort(
    (a, b) =>
      new Date(b.updatedAt || b.createdAt || 0).getTime() -
      new Date(a.updatedAt || a.createdAt || 0).getTime()
  );

const upsertNote = (notes, nextNote) => {
  const exists = notes.some(note => String(note.id) === String(nextNote.id));
  return sortNotes(
    exists
      ? notes.map(note => (String(note.id) === String(nextNote.id) ? nextNote : note))
      : [nextNote, ...notes]
  );
};

const useStyles = makeStyles(theme => ({
  headerContent: {
    alignItems: "center",
    display: "flex",
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      alignItems: "stretch",
      flexDirection: "column",
      gap: theme.spacing(1)
    }
  },
  headerButtons: {
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1),
      marginLeft: 0,
      "& > *": {
        margin: 0
      }
    }
  },
  titleRow: {
    alignItems: "center",
    display: "flex",
    gap: theme.spacing(1),
    minWidth: 0,
    "& h1, & h2, & h3, & h4, & h5, & h6": {
      margin: 0
    }
  },
  saveState: {
    alignItems: "center",
    display: "inline-flex",
    flex: "0 0 auto",
    fontSize: 17,
    height: 28,
    justifyContent: "center",
    lineHeight: 1,
    width: 28
  },
  saveStateSaving: {
    color: "#1677ff"
  },
  saveStateSaved: {
    color: "#22c55e",
    opacity: 1
  },
  mainPaper: {
    flex: 1,
    margin: 20,
    minHeight: 0,
    overflow: "hidden",
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("xs")]: {
      margin: theme.spacing(1)
    }
  },
  layout: {
    display: "grid",
    gridTemplateColumns: "300px minmax(0, 1fr)",
    minHeight: "calc(100vh - 150px)",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      minHeight: "auto"
    }
  },
  sidebar: {
    borderRight: `1px solid ${theme.palette.divider}`,
    minWidth: 0,
    overflowX: "hidden",
    overflowY: "auto",
    padding: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      borderRight: 0,
      borderBottom: `1px solid ${theme.palette.divider}`,
      maxHeight: 260
    }
  },
  noteList: {
    display: "grid",
    gap: theme.spacing(1),
    minWidth: 0
  },
  noteButton: {
    boxSizing: "border-box",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    background: theme.palette.background.paper,
    cursor: "pointer",
    display: "grid",
    gap: theme.spacing(0.5),
    gridTemplateRows: "auto auto",
    height: 68,
    minWidth: 0,
    overflow: "hidden",
    padding: theme.spacing(1.25),
    textAlign: "left",
    width: "100%",
    "&:hover": {
      background: theme.palette.action.hover
    }
  },
  noteButtonActive: {
    borderColor: theme.palette.primary.main,
    background: "rgba(63, 81, 181, 0.08)"
  },
  noteTitle: {
    fontWeight: 600,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  notePreview: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  editorPane: {
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    padding: theme.spacing(2)
  },
  editorHeader: {
    alignItems: "center",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5)
  },
  titleInput: {
    "& .MuiOutlinedInput-input": {
      fontSize: 22,
      fontWeight: 700
    }
  },
  editor: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    flex: 1,
    minHeight: 420,
    overflowY: "auto",
    padding: theme.spacing(1.5),
    "& .bn-container": {
      minHeight: 360
    }
  },
  emptyState: {
    alignItems: "center",
    color: theme.palette.text.secondary,
    display: "flex",
    justifyContent: "center",
    minHeight: 420,
    textAlign: "center"
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(3)
  }
}));

const Notes = () => {
  const classes = useStyles();
  const [notes, setNotes] = useState([]);
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [draft, setDraft] = useState(createEmptyNote());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingSave, setPendingSave] = useState(false);
  const [savedOnce, setSavedOnce] = useState(false);
  const [deleteNote, setDeleteNote] = useState(null);
  const saveTimerRef = useRef(null);
  const draftRef = useRef(draft);
  const loadingDraftRef = useRef(false);
  const creatingRef = useRef(false);
  const lastSavedSignatureRef = useRef("");
  const selectedNoteIdRef = useRef(selectedNoteId);

  const selectedNote = useMemo(
    () => notes.find(note => String(note.id) === String(selectedNoteId)) || null,
    [notes, selectedNoteId]
  );

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    selectedNoteIdRef.current = selectedNoteId;
  }, [selectedNoteId]);

  const loadNotes = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notes");
      const loadedNotes = data.notes || [];
      setNotes(sortNotes(loadedNotes));
      setSelectedNoteId(prevId =>
        loadedNotes.some(note => String(note.id) === String(prevId))
          ? prevId
          : loadedNotes[0]?.id || null
      );
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  useEffect(() => {
    if (selectedNote) {
      const nextDraft = {
        id: selectedNote.id,
        title: selectedNote.title || "",
        content: selectedNote.content || emptyContent
      };
      const nextSignature = getDocumentSignature(nextDraft);
      const currentDraft = draftRef.current;
      const currentSignature = getDocumentSignature(currentDraft);
      const isSameDraft = currentDraft?.id && String(currentDraft.id) === String(selectedNote.id);
      if (nextSignature === currentSignature) {
        setPendingSave(false);
        setSavedOnce(true);
        lastSavedSignatureRef.current = nextSignature;
        return;
      }
      if (isSameDraft && currentSignature !== lastSavedSignatureRef.current) {
        setSavedOnce(true);
        return;
      }
      if (isSameDraft && nextSignature === lastSavedSignatureRef.current) {
        setSavedOnce(true);
        return;
      }
      loadingDraftRef.current = true;
      setPendingSave(false);
      setSavedOnce(true);
      lastSavedSignatureRef.current = nextSignature;
      setDraft(nextDraft);
      return;
    }

    loadingDraftRef.current = true;
    setPendingSave(false);
    setSavedOnce(false);
    lastSavedSignatureRef.current = getDocumentSignature(createEmptyNote());
    setDraft(createEmptyNote());
  }, [selectedNote]);

  const handleNewNote = () => {
    setSelectedNoteId(null);
    loadingDraftRef.current = true;
    setPendingSave(false);
    setSavedOnce(false);
    lastSavedSignatureRef.current = getDocumentSignature(createEmptyNote());
    setDraft(createEmptyNote());
  };

  const persistDraft = useCallback(async snapshot => {
    const payload = {
      title: String(snapshot.title || "").trim(),
      content: snapshot.content || emptyContent
    };

    if (!payload.title) {
      setPendingSave(false);
      return;
    }
    if (!snapshot.id && !hasDocumentContent(payload.content)) {
      setPendingSave(false);
      return;
    }
    if (!snapshot.id && creatingRef.current) return;

    const requestSignature = getDocumentSignature({
      id: snapshot.id || null,
      ...payload
    });
    if (requestSignature === lastSavedSignatureRef.current) {
      setPendingSave(false);
      return;
    }

    if (!snapshot.id) creatingRef.current = true;
    setSaving(true);

    try {
      const { data } = snapshot.id
        ? await api.put(`/notes/${snapshot.id}`, payload)
        : await api.post("/notes", payload);

      lastSavedSignatureRef.current = getDocumentSignature({
        id: data.id,
        title: data.title,
        content: data.content || emptyContent
      });
      setSavedOnce(true);
      setNotes(prevNotes => upsertNote(prevNotes, data));

      if (!snapshot.id && !draftRef.current.id) {
        setSelectedNoteId(data.id);
        setDraft(currentDraft => ({
          ...currentDraft,
          id: data.id
        }));
      }
    } catch (err) {
      toastError(err);
    } finally {
      if (!snapshot.id) creatingRef.current = false;
      setPendingSave(false);
      setSaving(false);
    }
  }, []);

  useEffect(() => {
    if (loadingDraftRef.current) {
      loadingDraftRef.current = false;
      return undefined;
    }

    window.clearTimeout(saveTimerRef.current);
    setPendingSave(true);
    saveTimerRef.current = window.setTimeout(() => {
      persistDraft(draftRef.current);
    }, 700);

    return () => window.clearTimeout(saveTimerRef.current);
  }, [draft, persistDraft]);

  useEffect(
    () => () => {
      window.clearTimeout(saveTimerRef.current);
    },
    []
  );

  useEffect(() => {
    const socket = openSocket();

    socket.on("note", data => {
      if (data.action === "create" || data.action === "update") {
        if (!data.note?.id) return;
        setNotes(prevNotes => upsertNote(prevNotes, data.note));
        if (String(selectedNoteIdRef.current) === String(data.note.id)) {
          setPendingSave(false);
          setSavedOnce(true);
        }
        return;
      }

      if (data.action === "delete") {
        setNotes(prevNotes =>
          prevNotes.filter(note => String(note.id) !== String(data.noteId))
        );
        if (String(selectedNoteIdRef.current) === String(data.noteId)) {
          setPendingSave(false);
          setSelectedNoteId(null);
        }
      }
    });

    return () => socket.disconnect();
  }, []);

  const handleDelete = async () => {
    if (!deleteNote?.id) return;

    try {
      await api.delete(`/notes/${deleteNote.id}`);
      setNotes(prevNotes => prevNotes.filter(note => String(note.id) !== String(deleteNote.id)));
      if (String(selectedNoteId) === String(deleteNote.id)) {
        setSelectedNoteId(null);
      }
      toast.success("Nota eliminada.");
    } catch (err) {
      toastError(err);
    } finally {
      setDeleteNote(null);
    }
  };

  const renderNoteButton = note => {
    const text = getBlocksText(note.content?.blocks);

    return (
      <button
        key={note.id}
        type="button"
        className={`${classes.noteButton} ${
          String(note.id) === String(selectedNoteId) ? classes.noteButtonActive : ""
        }`}
        onClick={() => setSelectedNoteId(note.id)}
      >
        <div className={classes.noteTitle}>{note.title}</div>
        <div className={classes.notePreview}>
          {text || new Date(note.updatedAt || note.createdAt).toLocaleString()}
        </div>
      </button>
    );
  };

  const isSavingNote = pendingSave || saving;

  return (
    <MainContainer>
      <ConfirmationModal
        title={deleteNote && `Eliminar ${deleteNote.title}?`}
        open={Boolean(deleteNote)}
        onClose={() => setDeleteNote(null)}
        onConfirm={handleDelete}
      >
        Esta accion no se puede deshacer.
      </ConfirmationModal>

      <MainHeader>
        <div className={classes.headerContent}>
          <div className={classes.titleRow}>
            <Title>Notas</Title>
            {isSavingNote || savedOnce ? (
              <span
                className={`${classes.saveState} ${
                  isSavingNote ? classes.saveStateSaving : classes.saveStateSaved
                }`}
                aria-label={isSavingNote ? "Guardando" : "Guardado"}
                title={isSavingNote ? "Guardando" : "Guardado"}
              >
                {isSavingNote ? (
                  <CircularProgress color="inherit" size={17} />
                ) : (
                  <CheckCircleOutline fontSize="small" />
                )}
              </span>
            ) : null}
          </div>
          <MainHeaderButtonsWrapper className={classes.headerButtons}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={handleNewNote}
            >
              Nueva nota
            </Button>
          </MainHeaderButtonsWrapper>
        </div>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.layout}>
          <aside className={classes.sidebar}>
            {loading ? (
              <div className={classes.loading}>
                <CircularProgress size={24} />
              </div>
            ) : notes.length ? (
              <div className={classes.noteList}>{notes.map(renderNoteButton)}</div>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No hay notas creadas.
              </Typography>
            )}
          </aside>

          <section className={classes.editorPane}>
            <div className={classes.editorHeader}>
              <TextField
                className={classes.titleInput}
                value={draft.title}
                onChange={event => setDraft(prevDraft => ({ ...prevDraft, title: event.target.value }))}
                variant="outlined"
                placeholder="Titulo de la nota"
              />
              <div>
                {draft.id ? (
                  <IconButton disabled={saving} onClick={() => setDeleteNote(draft)}>
                    <DeleteOutline />
                  </IconButton>
                ) : null}
              </div>
            </div>

            <BlockDocument
              className={classes.editor}
              value={draft.content}
              onChange={content => setDraft(prevDraft => ({ ...prevDraft, content }))}
            />
          </section>
        </div>
      </Paper>
    </MainContainer>
  );
};

export default Notes;
